import React, { useState, useRef, useCallback, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../Information/cropImage";
import useFileUpload from "../../../../../../hooks/dataSenders/singleUpload";

const AddTeamMemberModal = ({
  show1,
  handleClose1,
  teamFormData,
  setTeamFormData,
  setLoader,
  editingIndex = null,
}) => {
  const fileInputRef = useRef(null);
  const { uploadFile } = useFileUpload();

  // Local state for new team member
  const [memberData, setMemberData] = useState({
    name: "",
    designation: "",
    bio: "",
    image: null,
  });

  // Validation errors
  const [errors, setErrors] = useState({
    name: "",
    designation: "",
    bio: "",
    image: "",
  });

  // Image cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // ✅ Update state when modal opens or editingIndex changes
  useEffect(() => {
    if (show1 && editingIndex !== null) {
      // Load existing team member data for editing
      const existingMember = teamFormData.team[editingIndex];
      setMemberData({
        name: existingMember.name || "",
        designation: existingMember.designation || "",
        bio: existingMember.bio || "",
        image: existingMember.image || null,
      });
      setCroppedImage(existingMember.image || null);
    } else if (show1 && editingIndex === null) {
      // Reset for adding new member
      resetForm();
    }
  }, [show1, editingIndex, teamFormData.team]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (3MB)
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        image: "File size must be less than 3MB",
      }));
      e.target.value = ""; // Clear the input
      return;
    }

    // Clear any previous errors
    setErrors((prev) => ({
      ...prev,
      image: "",
    }));

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropper(true);
    };
  };

  const base64ToFile = (base64String, filename = "team-member.png") => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleSaveCrop = async () => {
    try {
      const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = base64ToFile(cropped, "cropped-team-member.png");

      setShowCropper(false);
      setLoader(true);

      const url = await uploadFile(croppedFile, setLoader);

      setCroppedImage(url);
      setMemberData((prev) => ({ ...prev, image: url }));
      setErrors((prev) => ({ ...prev, image: "" }));

      setLoader(false);
    } catch (err) {
      console.error(err);
      setLoader(false);
      setErrors((prev) => ({
        ...prev,
        image: "Failed to upload image. Please try again.",
      }));
    }
  };

  const handleCloseCropper = () => {
    setShowCropper(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const removeDocument = () => {
    setCroppedImage(null);
    setMemberData((prev) => ({ ...prev, image: null }));
  };

  const handleChange = (field, value) => {
    if (field === "bio" && value.length > 300) {
      return;
    }

    setMemberData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      designation: "",
      bio: "",
      image: "",
    };

    let isValid = true;

    if (!memberData.name || memberData.name.trim() === "") {
      newErrors.name = "Full name is required";
      isValid = false;
    }

    if (!memberData.designation || memberData.designation.trim() === "") {
      newErrors.designation = "Designation is required";
      isValid = false;
    }

    // if (!memberData.bio || memberData.bio.trim() === "") {
    //   newErrors.bio = "Bio is required";
    //   isValid = false;
    // } else if (memberData.bio.trim().length < 10) {
    //   newErrors.bio = "Bio must be at least 10 characters";
    //   isValid = false;
    // }

    if (!memberData.image) {
      newErrors.image = "Image is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAdd = () => {
    if (!validateForm()) {
      return;
    }

    if (editingIndex !== null) {
      const updatedTeam = [...teamFormData.team];
      updatedTeam[editingIndex] = {
        ...updatedTeam[editingIndex],
        name: memberData.name,
        designation: memberData.designation,
        bio: memberData.bio,
        image: memberData.image,
      };

      setTeamFormData((prev) => ({
        ...prev,
        team: updatedTeam,
      }));
    } else {
      const isMemberEmpty = (member) => {
        return (
          (!member.name || member.name.trim() === "") &&
          (!member.designation || member.designation.trim() === "") &&
          (!member.bio || member.bio.trim() === "") &&
          !member.image
        );
      };

      const isFirstMemberEmpty =
        teamFormData.team.length > 0 && isMemberEmpty(teamFormData.team[0]);

      const newMember = {
        name: memberData.name,
        designation: memberData.designation,
        bio: memberData.bio,
        image: memberData.image,
      };

      if (isFirstMemberEmpty) {
        const updatedTeam = [...teamFormData.team];
        updatedTeam[0] = newMember;

        setTeamFormData((prev) => ({
          ...prev,
          team: updatedTeam,
        }));
      } else {
        setTeamFormData((prev) => ({
          ...prev,
          team: [...prev.team, newMember],
        }));
      }
    }

    handleClose1();
    resetForm();
  };

  const resetForm = () => {
    setMemberData({
      name: "",
      designation: "",
      bio: "",
      image: null,
    });
    setCroppedImage(null);
    setErrors({
      name: "",
      designation: "",
      bio: "",
      image: "",
    });
  };

  const handleCancel = () => {
    resetForm();
    handleClose1();
  };

  return (
    <>
      <Modal
        className="publish-modall addteammembermodal"
        show={show1}
        onHide={handleCancel}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingIndex !== null ? "Edit Team Member" : "Add Team Member"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="publish_modal">
            <div className="team_member">
              <div className="innner_team">
                <div className="left_team">
                  <div className="edit_img">
                    {croppedImage ? (
                      <img
                        src={croppedImage}
                        alt="cropped preview"
                        className="teamPreview"
                        onClick={openFilePicker}
                        style={{ cursor: "pointer" }}
                      />
                    ) : (
                      <img
                        src="/assets/downlodnew.svg"
                        onClick={openFilePicker}
                        className="ediiiiit"
                        style={{ cursor: "pointer" }}
                      />
                    )}
                  </div>
                  <div>
                    <p>800×800 resolution • Max 3MB • JPEG/PNG</p>
                    <div>
                      {!croppedImage && (
                        <button onClick={openFilePicker}>Upload Image</button>
                      )}

                      {croppedImage && (
                        <div className="replacesbutton">
                          <button onClick={openFilePicker}>Replace</button>
                          <div
                            className="delllbuts"
                            onClick={removeDocument}
                            style={{ cursor: "pointer" }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M2 4H14"
                                stroke="#FC4F52"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12.6663 4V13.3333C12.6663 14 11.9997 14.6667 11.333 14.6667H4.66634C3.99967 14.6667 3.33301 14 3.33301 13.3333V4"
                                stroke="#FC4F52"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M5.33301 3.9987V2.66536C5.33301 1.9987 5.99967 1.33203 6.66634 1.33203H9.33301C9.99967 1.33203 10.6663 1.9987 10.6663 2.66536V3.9987"
                                stroke="#FC4F52"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M6.66699 7.33203V11.332"
                                stroke="#FC4F52"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9.33301 7.33203V11.332"
                                stroke="#FC4F52"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.image && (
                      <p
                        className="errromsgz"
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "8px",
                        }}
                      >
                        {errors.image}
                      </p>
                    )}
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={fileInputRef}
                    onChange={onFileChange}
                  />
                </div>

                <div className="under_div">
                  <div className="under_review">
                    <div>
                      <h3>
                        Full Name <span style={{ color: "red" }}>*</span>
                      </h3>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={memberData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        className={errors.name ? "error" : ""}
                      />
                      {errors.name && (
                        <p
                          className="errromsgz"
                          style={{
                            color: "red",
                            fontSize: "12px",
                            textAlign: "start",
                            alignItems: "flex-start",
                            marginTop: "4px",
                          }}
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <h3>
                        Designation <span style={{ color: "red" }}>*</span>
                      </h3>
                      <input
                        type="text"
                        placeholder="Enter Designation"
                        value={memberData.designation}
                        onChange={(e) =>
                          handleChange("designation", e.target.value)
                        }
                        className={errors.designation ? "error" : ""}
                      />
                      {errors.designation && (
                        <p
                          className="errromsgz"
                          style={{
                            color: "red",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {errors.designation}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3>
                      Bio
                      {/* <span style={{ color: "red" }}>*</span> */}
                    </h3>
                    <textarea
                      placeholder="Team member bio"
                      rows="4"
                      cols="50"
                      value={memberData.bio}
                      onChange={(e) => handleChange("bio", e.target.value)}
                      maxLength={300}
                      className={errors.bio ? "error" : ""}
                    />
                    {errors.bio && (
                      <p
                        className="errromsgz"
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.bio}
                      </p>
                    )}
                  </div>
                  <p className="charactersize">
                    {memberData.bio.length}/300 Character
                  </p>
                </div>
              </div>
            </div>

            <div className="publish_buttons">
              <button onClick={handleCancel} className="cancel_button">
                Cancel
              </button>
              <button className="confirm_button" onClick={handleAdd}>
                {editingIndex !== null ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Cropper Modal */}
      <Modal
        className="publish-modall"
        show={showCropper}
        onHide={handleCloseCropper}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="publish_modal">
            <div className="croopppy">
              <div className="cropmodal">
                {imageSrc && (
                  <div
                    className="crop-container inercrop"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "240px",
                      background: "#E7EEF2",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                )}
              </div>
              <p>Drag to reposition image</p>

              <div className="zoom-slider">
                <h5>Zoom</h5>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "8px",
                    borderRadius: "10px",
                    appearance: "none",
                    background: `linear-gradient(to right, #329879 0%, #329879 ${
                      ((zoom - 1) / 2) * 100
                    }%, #d0d7de ${((zoom - 1) / 2) * 100}%, #d0d7de 100%)`,
                    outline: "none",
                  }}
                />
              </div>

              <div className="publish_buttons">
                <button className="cancel_button" onClick={handleCloseCropper}>
                  Cancel
                </button>
                <button className="confirm_button" onClick={handleSaveCrop}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddTeamMemberModal;
